<template>
  <div class="row">
    <div class="col-12">
      <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing radius-7">
        <div class="card-body">
          <div class="card-head bg-white d-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 radius-7 mb-4">
            <div>
              <h4 class="mb-1">Estacionamento</h4>
              <p class="mb-0 text-muted">Controle de placas no pátio, pagamentos e regras de cobrança.</p>
            </div>
            <button class="btn btn-outline-primary" @click="refreshAll" :disabled="getParkingLoading">
              Atualizar
            </button>
          </div>

          <div v-if="getParkingError" class="alert alert-danger">{{ normalizeMessage(getParkingError) }}</div>
          <div v-if="getParkingSuccess" class="alert alert-success">{{ getParkingSuccess }}</div>

          <ul class="nav nav-tabs bg-white border-0 rounded-0 radius-7" role="tablist">
            <li class="nav-item" role="presentation">
              <button class="nav-link active fw-bold" data-bs-toggle="tab" data-bs-target="#parking-open" type="button">
                Placas no estacionamento
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link fw-bold" data-bs-toggle="tab" data-bs-target="#parking-history" type="button">
                Histórico de pagamentos
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link fw-bold" data-bs-toggle="tab" data-bs-target="#parking-settings" type="button">
                Configurações
              </button>
            </li>
          </ul>

          <div class="tab-content p-15 p-sm-20 p-md-25">
            <div class="tab-pane fade show active" id="parking-open" role="tabpanel">
              <div v-if="getParkingLoading" class="text-center p-5">
                <div class="spinner-border text-primary" role="status"></div>
                <p class="mt-3">Carregando veículos...</p>
              </div>

              <div v-else-if="getParkingOpenSessions.length === 0" class="empty-state">
                Nenhuma placa em aberto no estacionamento.
              </div>

              <div v-else>
                <div class="table-responsive">
                  <table class="table align-middle">
                    <thead>
                      <tr>
                        <th>Placa</th>
                        <th>Entrada</th>
                        <th>Status</th>
                        <th>Valor atual</th>
                        <th>Link de pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="session in paginatedOpenSessions" :key="session.id">
                        <td class="fw-bold plate-text">{{ session.plate }}</td>
                        <td>{{ session.entered_at }}</td>
                        <td>
                          <span class="badge" :class="session.status === 'paid' ? 'bg-success' : 'bg-warning text-dark'">
                            {{ session.status === 'paid' ? 'Pago' : 'Em aberto' }}
                          </span>
                        </td>
                        <td>{{ session.amount_due_formatted }}</td>
                        <td>
                          <a :href="paymentLink(session)" target="_blank" class="btn btn-sm btn-outline-primary">
                            Abrir
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="pagination-row">
                  <span>{{ paginationSummary('open') }}</span>
                  <div class="pagination-actions">
                    <button class="btn btn-sm btn-outline-secondary" @click="previousPage('open')" :disabled="openPage === 1">
                      Anterior
                    </button>
                    <span>Página {{ openPage }} de {{ openTotalPages }}</span>
                    <button class="btn btn-sm btn-outline-secondary" @click="nextPage('open')" :disabled="openPage >= openTotalPages">
                      Próxima
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="tab-pane fade" id="parking-history" role="tabpanel">
              <div v-if="getParkingPaymentHistory.length === 0" class="empty-state">
                Nenhum pagamento de estacionamento encontrado.
              </div>

              <div v-else>
                <div class="table-responsive">
                  <table class="table align-middle">
                    <thead>
                      <tr>
                        <th>Placa</th>
                        <th>Valor</th>
                        <th>Forma</th>
                        <th>Pago em</th>
                        <th>ID Mercado Pago</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="payment in paginatedPaymentHistory" :key="payment.id">
                        <td class="fw-bold plate-text">{{ payment.plate }}</td>
                        <td>{{ payment.amount_formatted }}</td>
                        <td>{{ payment.payment_method || '-' }}</td>
                        <td>{{ payment.paid_at }}</td>
                        <td>{{ payment.mercado_pago_payment_id || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="pagination-row">
                  <span>{{ paginationSummary('history') }}</span>
                  <div class="pagination-actions">
                    <button class="btn btn-sm btn-outline-secondary" @click="previousPage('history')" :disabled="historyPage === 1">
                      Anterior
                    </button>
                    <span>Página {{ historyPage }} de {{ historyTotalPages }}</span>
                    <button class="btn btn-sm btn-outline-secondary" @click="nextPage('history')" :disabled="historyPage >= historyTotalPages">
                      Próxima
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="tab-pane fade" id="parking-settings" role="tabpanel">
              <form @submit.prevent="saveSettings">
                <div class="row">
                  <div class="col-lg-4 mb-3">
                    <label class="form-label">Nome da regra</label>
                    <input v-model="rateForm.name" type="text" class="form-control" placeholder="Tabela padrão" />
                  </div>
                  <div class="col-lg-4 mb-3">
                    <label class="form-label">Tipo de cobrança</label>
                    <select v-model="rateForm.billing_type" class="form-select">
                      <option value="hourly">Por hora</option>
                      <option value="fixed_then_hourly">Fixo até X horas + adicional</option>
                    </select>
                  </div>
                  <div class="col-lg-4 mb-3">
                    <label class="form-label">Tolerância (minutos)</label>
                    <input v-model.number="rateForm.grace_period_minutes" type="number" min="0" class="form-control" />
                  </div>
                </div>

                <div class="row">
                  <div class="col-lg-4 mb-3">
                    <label class="form-label">Valor por hora</label>
                    <input v-model.number="rateForm.hourly_rate" type="number" min="0" step="0.01" class="form-control" />
                  </div>
                  <div class="col-lg-4 mb-3" v-if="rateForm.billing_type === 'fixed_then_hourly'">
                    <label class="form-label">Fixo até quantas horas</label>
                    <input v-model.number="rateForm.fixed_until_hours" type="number" min="1" class="form-control" />
                  </div>
                  <div class="col-lg-4 mb-3" v-if="rateForm.billing_type === 'fixed_then_hourly'">
                    <label class="form-label">Valor fixo</label>
                    <input v-model.number="rateForm.fixed_value" type="number" min="0" step="0.01" class="form-control" />
                  </div>
                  <div class="col-lg-4 mb-3" v-if="rateForm.billing_type === 'fixed_then_hourly'">
                    <label class="form-label">Valor por hora adicional</label>
                    <input v-model.number="rateForm.additional_hourly_rate" type="number" min="0" step="0.01" class="form-control" />
                  </div>
                </div>

                <h5 class="mt-4 mb-3">Câmeras do estacionamento</h5>
                <div v-if="cameraForm.length === 0" class="empty-state">
                  Nenhuma câmera disponível para configurar.
                </div>
                <div v-else class="table-responsive">
                  <table class="table align-middle">
                    <thead>
                      <tr>
                        <th>Câmera</th>
                        <th>Instalação</th>
                        <th>Usar no estacionamento</th>
                        <th>Direção</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="camera in cameraForm" :key="camera.camera_id">
                        <td>
                          <div class="fw-semibold">{{ camera.name }}</div>
                          <small class="text-muted">{{ camera.token }}</small>
                        </td>
                        <td>{{ camera.installation_name || '-' }}</td>
                        <td>
                          <input v-model="camera.active" class="form-check-input" type="checkbox" />
                        </td>
                        <td>
                          <select v-model="camera.direction" class="form-select direction-select" :disabled="!camera.active">
                            <option value="entry">Entrada</option>
                            <option value="exit">Saída</option>
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="d-flex justify-content-end mt-3">
                  <button class="btn btn-primary" type="submit" :disabled="getParkingSettingsLoading">
                    Salvar configurações
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Pusher from 'pusher-js';

export default {
  name: "ParkingContent",
  data() {
    return {
      rateForm: {
        name: 'Tabela padrão',
        billing_type: 'hourly',
        hourly_rate: 0,
        fixed_until_hours: 3,
        fixed_value: 0,
        additional_hourly_rate: 0,
        grace_period_minutes: 0
      },
      cameraForm: [],
      openPage: 1,
      historyPage: 1,
      perPage: 10,
      parkingPusher: null,
      parkingChannel: null,
      currentParkingChannelName: null,
      currentParkingEventName: null,
      realtimeRefreshTimer: null
    };
  },
  computed: {
    ...mapGetters([
      "getParkingOpenSessions",
      "getParkingPaymentHistory",
      "getParkingRate",
      "getParkingCameras",
      "getParkingLoading",
      "getParkingSettingsLoading",
      "getParkingError",
      "getParkingSuccess",
      "getCompany_id"
    ]),
    paginatedOpenSessions() {
      return this.paginate(this.getParkingOpenSessions, this.openPage);
    },
    paginatedPaymentHistory() {
      return this.paginate(this.getParkingPaymentHistory, this.historyPage);
    },
    openTotalPages() {
      return this.totalPages(this.getParkingOpenSessions);
    },
    historyTotalPages() {
      return this.totalPages(this.getParkingPaymentHistory);
    }
  },
  watch: {
    getParkingRate: {
      handler(value) {
        if (!value) {
          return;
        }

        this.rateForm = {
          name: value.name || 'Tabela padrão',
          billing_type: value.billing_type || 'hourly',
          hourly_rate: Number(value.hourly_rate || 0),
          fixed_until_hours: Number(value.fixed_until_hours || 3),
          fixed_value: Number(value.fixed_value || 0),
          additional_hourly_rate: Number(value.additional_hourly_rate || 0),
          grace_period_minutes: Number(value.grace_period_minutes || 0)
        };
      },
      immediate: true
    },
    getParkingCameras: {
      handler(value) {
        this.cameraForm = (value || []).map(camera => ({
          camera_id: camera.id,
          name: camera.name,
          token: camera.token,
          installation_name: camera.installation_name,
          active: Boolean(camera.parking_active),
          direction: camera.direction || 'entry'
        }));
      },
      immediate: true
    },
    getParkingOpenSessions() {
      this.openPage = Math.min(this.openPage, this.openTotalPages);
    },
    getParkingPaymentHistory() {
      this.historyPage = Math.min(this.historyPage, this.historyTotalPages);
    },
    getCompany_id() {
      this.stopParkingRealtime();
      this.setupParkingRealtime();
    }
  },
  mounted() {
    this.refreshAll();
    this.setupParkingRealtime();
  },
  beforeUnmount() {
    this.stopParkingRealtime();
    this.clearRealtimeRefreshTimer();
  },
  methods: {
    ...mapActions([
      "setParkingDashboard",
      "setParkingSettings",
      "saveParkingSettings"
    ]),
    refreshAll() {
      this.setParkingDashboard();
      this.setParkingSettings();
    },
    setupParkingRealtime() {
      if (!this.getCompany_id || this.parkingPusher) {
        return;
      }

      this.parkingPusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
        cluster: process.env.VUE_APP_PUSHER_CLUSTER,
        forceTLS: true
      });

      const channelName = `parking-${this.getCompany_id}`;
      const eventName = `parking-update-${this.getCompany_id}`;
      const channel = this.parkingPusher.subscribe(channelName);

      this.parkingChannel = channel;
      this.currentParkingChannelName = channelName;
      this.currentParkingEventName = eventName;

      this.parkingPusher.connection.bind('connected', () => {
        console.info('Pusher conectado para estacionamento:', channelName);
      });

      channel.bind('pusher:subscription_succeeded', () => {
        console.info('Canal de estacionamento inscrito:', channelName);
      });

      channel.bind('pusher:subscription_error', error => {
        console.error('Erro ao inscrever canal de estacionamento:', channelName, error);
      });

      channel.bind(eventName, () => {
        console.info('Evento realtime do estacionamento recebido:', eventName);
        this.scheduleRealtimeRefresh();
      });
    },
    stopParkingRealtime() {
      if (this.parkingChannel && this.currentParkingEventName) {
        this.parkingChannel.unbind(this.currentParkingEventName);
      }

      if (this.parkingPusher && this.currentParkingChannelName) {
        this.parkingPusher.unsubscribe(this.currentParkingChannelName);
        this.parkingPusher.disconnect();
      }

      this.parkingPusher = null;
      this.parkingChannel = null;
      this.currentParkingChannelName = null;
      this.currentParkingEventName = null;
    },
    scheduleRealtimeRefresh() {
      this.clearRealtimeRefreshTimer();
      this.realtimeRefreshTimer = window.setTimeout(() => {
        this.setParkingDashboard();
      }, 300);
    },
    clearRealtimeRefreshTimer() {
      if (this.realtimeRefreshTimer) {
        clearTimeout(this.realtimeRefreshTimer);
        this.realtimeRefreshTimer = null;
      }
    },
    saveSettings() {
      this.saveParkingSettings({
        rate: this.rateForm,
        cameras: this.cameraForm.map(camera => ({
          camera_id: camera.camera_id,
          active: camera.active,
          direction: camera.direction
        }))
      });
    },
    paymentLink(session) {
      const token = session && session.public_token
        ? session.public_token
        : this.extractToken(session && session.payment_url);

      if (!token) {
        return session && session.payment_url ? session.payment_url : '/parking/payment';
      }

      return `${window.location.origin}/parking/payment?token=${encodeURIComponent(token)}`;
    },
    extractToken(paymentUrl) {
      if (!paymentUrl) {
        return '';
      }

      try {
        const url = new URL(paymentUrl);
        return url.searchParams.get('token') || '';
      } catch (error) {
        return '';
      }
    },
    paginate(items, page) {
      const start = (page - 1) * this.perPage;
      return (items || []).slice(start, start + this.perPage);
    },
    totalPages(items) {
      return Math.max(1, Math.ceil((items || []).length / this.perPage));
    },
    previousPage(type) {
      if (type === 'open') {
        this.openPage = Math.max(1, this.openPage - 1);
        return;
      }

      this.historyPage = Math.max(1, this.historyPage - 1);
    },
    nextPage(type) {
      if (type === 'open') {
        this.openPage = Math.min(this.openTotalPages, this.openPage + 1);
        return;
      }

      this.historyPage = Math.min(this.historyTotalPages, this.historyPage + 1);
    },
    paginationSummary(type) {
      const items = type === 'open' ? this.getParkingOpenSessions : this.getParkingPaymentHistory;
      const page = type === 'open' ? this.openPage : this.historyPage;
      const total = (items || []).length;
      const start = total === 0 ? 0 : ((page - 1) * this.perPage) + 1;
      const end = Math.min(page * this.perPage, total);

      return `Mostrando ${start}-${end} de ${total}`;
    },
    normalizeMessage(message) {
      if (typeof message === 'string') {
        return message;
      }

      if (message && message.message) {
        return message.message;
      }

      return "Ocorreu um erro inesperado.";
    }
  }
};
</script>

<style scoped>
.empty-state {
  background: #f8faff;
  border: 1px solid #eff3fc;
  border-radius: 7px;
  color: #516082;
  padding: 28px;
  text-align: center;
}

.plate-text {
  letter-spacing: 1px;
}

.direction-select {
  min-width: 130px;
}

.pagination-row {
  align-items: center;
  color: #60708f;
  display: flex;
  font-size: 0.9rem;
  gap: 12px;
  justify-content: space-between;
  margin-top: 16px;
}

.pagination-actions {
  align-items: center;
  display: flex;
  gap: 10px;
}

@media (max-width: 575px) {
  .pagination-row {
    align-items: stretch;
    flex-direction: column;
  }

  .pagination-actions {
    justify-content: space-between;
  }
}
</style>
